import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { ConfigServerService, CustomHttp, toHTTPParam } from '../../../core';
import { ExpedicaoSearch } from '../expedicao-search';
import { ExpedicaoSearchPessoa } from '../expedicao-search-pessoa';
import { ExpedicaoIniciarSeparacao } from '../iniciar-separacao/expedicao-iniciar-separacao';
import { ExpedicaoTerminarSeparacao } from '../terminar-separacao/expedicao-terminar-separacao';
import { ExpedicaoSequenciaUpdate } from '../expedicao-sequencia-update';
import { ExpedicaoFilter } from '../filter';
import { map } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class ExpedicaoHttpService {
    private pathApi: string;

    constructor(private customHttp: CustomHttp,
                configServerService: ConfigServerService) {
        this.pathApi = `${configServerService.getPathApi()}/pedidoexpedicao`;
    }

    /** Libera o pedido informado para expedição */
    liberar(id: number): Observable<string> {
        return this.customHttp.put<string>(`${this.pathApi}/${id}/liberar`, null);
    }

    /** Cancela a liberação do pedido informado para expedição */
    cancelarLiberacao(id: number): Observable<string> {
        return this.customHttp.delete<string>(`${this.pathApi}/${id}/liberar`);
    }

    /** Pesquisa os pedidos que estão na expedição "Aguardando Produção" */
    searchAguardandoProducao(filter: ExpedicaoFilter): Observable<ExpedicaoSearch[]> {
        // Params
        let params = new HttpParams();
        if (filter) {
            params = filter.createParams(params);
        }

        return this.customHttp.get<ExpedicaoSearch[]>(`${this.pathApi}/0/aguardandoproducao`, {params})
                    .pipe(
                        map((pedidos: ExpedicaoSearch[]) => {
                            if (!pedidos) {
                                return [];
                            }

                            return pedidos.map((pedido: ExpedicaoSearch) => new ExpedicaoSearch(pedido));
                        })
                    );
    }

    /** Pesquisa os pedidos que estão na expedição "A Separar" */
    searchASeparar(filter: ExpedicaoFilter): Observable<ExpedicaoSearch[]> {
        // Params
        let params = new HttpParams();
        if (filter) {
            params = filter.createParams(params);
        }

        return this.customHttp.get<ExpedicaoSearch[]>(`${this.pathApi}/0/aseparar`, {params})
                    .pipe(
                        map((pedidos: ExpedicaoSearch[]) => {
                            if (!pedidos) {
                                return [];
                            }

                            return pedidos.map((pedido: ExpedicaoSearch) => new ExpedicaoSearch(pedido));
                        })
                    );
    }

    /** Pesquisa um pedido que está na expedição "A Separar" */
    searchOneASeparar(idPedido: number): Observable<ExpedicaoSearch> {
        return this.customHttp.get<ExpedicaoSearch>(`${this.pathApi}/${idPedido}/aseparar/one`)
                    .pipe(
                        map((pedido: ExpedicaoSearch) => {
                            if (!pedido) {
                                return null;
                            }

                            return new ExpedicaoSearch(pedido);
                        })
                    );
    }

    /** Pesquisa os pedidos de uma pessoa que estão na expedição "A Separar" */
    searchASepararPessoa(idPedido: number, idPessoa: number): Observable<ExpedicaoSearchPessoa> {
        return this.customHttp.get<ExpedicaoSearchPessoa>(`${this.pathApi}/${idPedido}/aseparar/pessoa/${idPessoa}`)
                    .pipe(
                        map((response: ExpedicaoSearchPessoa) => {
                            if (!response) {
                                return null;
                            }

                            return new ExpedicaoSearchPessoa(response);
                        })
                    );
    }

    /** Pesquisa os pedidos que estão na expedição "Em Separação" */
    searchEmSeparacao(filter: ExpedicaoFilter): Observable<ExpedicaoSearch[]> {
        // Params
        let params = new HttpParams();
        if (filter) {
            params = filter.createParams(params);
        }

        return this.customHttp.get<ExpedicaoSearch[]>(`${this.pathApi}/0/emseparacao`, {params})
                    .pipe(
                        map((pedidos: ExpedicaoSearch[]) => {
                            if (!pedidos) {
                                return [];
                            }

                            return pedidos.map((pedido: ExpedicaoSearch) => new ExpedicaoSearch(pedido));
                        })
                    );
    }

    /** Pesquisa um pedido que está na expedição "Em Separação" */
    searchOneEmSeparacao(idPedido: number): Observable<ExpedicaoSearch> {
        return this.customHttp.get<ExpedicaoSearch>(`${this.pathApi}/${idPedido}/emseparacao/one`)
                    .pipe(
                        map((pedido: ExpedicaoSearch) => {
                            if (!pedido) {
                                return null;
                            }

                            return new ExpedicaoSearch(pedido);
                        })
                    );
    }

    /** Pesquisa os pedidos de uma pessoa que estão na expedição "Em Separação" */
    searchEmSeparacaoPessoa(idPedido: number, idPessoa: number): Observable<ExpedicaoSearchPessoa> {
        return this.customHttp.get<ExpedicaoSearchPessoa>(`${this.pathApi}/${idPedido}/emseparacao/pessoa/${idPessoa}`)
                    .pipe(
                        map((response: ExpedicaoSearchPessoa) => {
                            if (!response) {
                                return null;
                            }

                            return new ExpedicaoSearchPessoa(response);
                        })
                    );
    }

    /** Pesquisa os pedidos que estão na expedição "Separados" */
    searchSeparados(filter: ExpedicaoFilter): Observable<ExpedicaoSearch[]> {
        // Params
        let params = new HttpParams();
        if (filter) {
            params = filter.createParams(params);
        }

        return this.customHttp.get<ExpedicaoSearch[]>(`${this.pathApi}/0/separados`, {params})
                    .pipe(
                        map((pedidos: ExpedicaoSearch[]) => {
                            if (!pedidos) {
                                return [];
                            }

                            return pedidos.map((pedido: ExpedicaoSearch) => new ExpedicaoSearch(pedido));
                        })
                    );
    }

    /** Pesquisa um pedido que está na expedição "Separados" */
    searchOneSeparados(idPedido: number, filter: ExpedicaoFilter): Observable<ExpedicaoSearch> {
        // Params
        let params = new HttpParams();
        if (filter) {
            params = filter.createParams(params);
        }
        return this.customHttp.get<ExpedicaoSearch>(`${this.pathApi}/${idPedido}/separados/one`, {params})
                    .pipe(
                        map((pedido: ExpedicaoSearch) => {
                            if (!pedido) {
                                return null;
                            }

                            return new ExpedicaoSearch(pedido);
                        })
                    );
    }

    /** Pesquisa os pedidos de uma pessoa que estão na expedição "Separados" */
    searchSeparadosPessoa(idPedido: number, idPessoa: number, dataSeparacaoInicial: Date, dataSeparacaoFinal: Date): Observable<ExpedicaoSearchPessoa> {
        let params = new HttpParams();
        params = params.append('dataSeparacaoInicial', dataSeparacaoInicial ? toHTTPParam(dataSeparacaoInicial) : null);
        params = params.append('dataSeparacaoFinal', dataSeparacaoFinal ? toHTTPParam(dataSeparacaoFinal) : null);

        return this.customHttp.get<ExpedicaoSearchPessoa>(`${this.pathApi}/${idPedido}/separados/pessoa/${idPessoa}`, {params})
                    .pipe(
                        map((response: ExpedicaoSearchPessoa) => {
                            if (!response) {
                                return null;
                            }

                            return new ExpedicaoSearchPessoa(response);
                        })
                    );
    }


    /** Pesquisa os pedidos que terminaram a sua expedição mas estão aguarando liberação do faturamento para serem coletados */
    searchAColetar(filter: ExpedicaoFilter): Observable<ExpedicaoSearch[]> {
       // Params
       let params = new HttpParams();
        if (filter) {
            params = filter.createParams(params);
        }

        return this.customHttp.get<ExpedicaoSearch[]>(`${this.pathApi}/0/liberarcoleta`, {params})
                    .pipe(
                        map((pedidos: ExpedicaoSearch[]) => {
                            if (!pedidos) {
                                return [];
                            }

                            return pedidos.map((pedido: ExpedicaoSearch) => new ExpedicaoSearch(pedido));
                        })
                    );
    }

    /** Pesquisa os pedidos de uma mesma pessoa que terminaram a sua expedição mas estão aguarando liberação do faturamento para serem coletados */
    searchAColetarPessoa(idPedido: number, idPessoa: number): Observable<ExpedicaoSearchPessoa> {
        return this.customHttp.get<ExpedicaoSearchPessoa>(`${this.pathApi}/${idPedido}/liberarcoleta/pessoa/${idPessoa}`)
                    .pipe(
                        map((response: ExpedicaoSearchPessoa) => {
                            if (!response) {
                                return null;
                            }

                            return new ExpedicaoSearchPessoa(response);
                        })
                    );
    }

    /** Inicia a separação de um pedido na expedição */
    iniciarSeparacao(entity: ExpedicaoIniciarSeparacao): Observable<string> {
        return this.customHttp.put<string>(`${this.pathApi}/0/iniciarseparacao`, entity);
    }

    /** Cancela o início da separação de um pedido na expedição */
    cancelarInicioSeparacao(id: number): Observable<string> {
        return this.customHttp.delete<string>(`${this.pathApi}/${id}/iniciarseparacao`);
    }


    /** Termina a separação de um pedido na expedição */
    terminarSeparacao(entity: ExpedicaoTerminarSeparacao): Observable<string> {
        return this.customHttp.put<string>(`${this.pathApi}/0/terminarseparacao`, entity);
    }

    /** Cancela o término da separação de um pedido na expedição */
    cancelarTerminoSeparacao(id: number): Observable<string> {
        return this.customHttp.delete<string>(`${this.pathApi}/${id}/terminarseparacao`);
    }

    /** Atualiza as sequências dos pedidos na expedição com base na atualização realizada no pedido informado */
    updateSequencia(entityUpdate: ExpedicaoSequenciaUpdate): Observable<string> {
        return this.customHttp.put<string>(`${this.pathApi}/0/sequencia`, entityUpdate);
    }
}
